import { orderBy } from 'firebase/firestore';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Breadcrumb from '../../../../components/bootstrap/Breadcrumb';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardBody } from '../../../../components/bootstrap/Card';
import SelectEstablishment from '../../../../components/MyCustom/SelectEstablishment';
import SelectStore from '../../../../components/MyCustom/SelectStore';
import TableCustom from '../../../../components/MyCustom/TableCustom';
import ModalStoreCategorySchedule from '../../../../components/modals/StoreCategories/ModalSchedule';
import { DefaultContext } from '../../../../contexts/default';
import CategoryDB from '../../../../database/wrappers/category';
import StoreCategoryDB from '../../../../database/wrappers/storeCategory';
import UserHistory from '../../../../database/wrappers/userHistory';
import { getCategoryScheduleDescription } from '../../../../helpers/utils/categorySchedule';
import Page from '../../../../layout/Page/Page';
import PageWrapper from '../../../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft } from '../../../../layout/SubHeader/SubHeader';
import { demoPages } from '../../../../menu';

const StoreCategories = () => {
  const { establishment, store, onShowAlert, user } = useContext(DefaultContext);
  const [categories, setCategories] = useState(null);
  const [storeCategoriesDicionary, setStoreCategoriesDicionary] = useState(null);
  const [categoriesRender, setCategoriesRender] = useState([]);
  const [categorySelected, setCategorySelected] = useState(null);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);

  useEffect(() => {
    if (!establishment || !store) return;

    const onSubscribe = new CategoryDB(establishment.id).on(setCategories, orderBy('name', 'asc'));
    return onSubscribe;
  }, [establishment, store]);

  useEffect(() => {
    if (!establishment || !store) return;

    const onSubscribe = new StoreCategoryDB(establishment.id, store.id).on((datas) => {
      const dicionary = {};
      datas.forEach((data) => {
        dicionary[data.id] = data;
      });
      setStoreCategoriesDicionary(dicionary);
    });
    return onSubscribe;
  }, [establishment, store]);

  useEffect(() => {
    if (categories === null || storeCategoriesDicionary === null) return;

    setCategoriesRender(
      categories
        .map((category) => {
          const storeCategory = storeCategoriesDicionary[category.id];

          return {
            ...category,
            ...storeCategory,
            active: category.active && (storeCategory?.active ?? true),
            storeActive: storeCategory?.active ?? true,
            storeCategoryExists: Boolean(storeCategory),
          };
        })
        .sort((categoryA, categoryB) => {
          const schedulePriority = Number(Boolean(categoryB.scheduleEnabled))
            - Number(Boolean(categoryA.scheduleEnabled));

          if (schedulePriority !== 0) return schedulePriority;

          return (categoryA.name || '').localeCompare(categoryB.name || '', 'pt-BR');
        }),
    );
  }, [categories, storeCategoriesDicionary]);

  const handleChangeStatus = useCallback((item) => {
    const data = { active: !item.storeActive };
    const storeCategoryDB = new StoreCategoryDB(establishment.id, store.id);
    const save = item.storeCategoryExists
      ? storeCategoryDB.update(item.id, data)
      : storeCategoryDB.define(item.id, data);

    save
      .then(() => {
        new UserHistory(establishment.id, user, 'a Categoria', store.name)
          .update(item.id, { data, selected: item })
          .finally(() => {});
      })
      .catch(() => onShowAlert({
        title: 'ERRO!',
        msm: 'Não foi possível atualizar o status da categoria na loja',
        icon: 'Error',
        color: 'danger',
      }));
  }, [establishment, store, onShowAlert, user]);

  const handleOpenScheduleModal = useCallback((item) => {
    setCategorySelected(item);
    setOpenScheduleModal(true);
  }, []);

  const columns = useMemo(() => ([
    {
      label: 'Nome',
      field: 'name',
    },
    {
      label: 'Exibição na loja',
      field: '{row}',
      format: row => (
        <Button
          isLink
          color={row.storeActive ? 'success' : 'danger'}
          icon='Circle'
          onClick={() => handleChangeStatus(row)}
          className='text-nowrap'
        >
          {row.storeActive ? 'Habilitada' : 'Desabilitada'}
        </Button>
      ),
      formatExport: row => row.storeActive ? 'Habilitada' : 'Desabilitada',
    },
    {
      label: 'Horários em que fica oculta',
      field: '{row}',
      format: row => getCategoryScheduleDescription(row),
      formatExport: row => getCategoryScheduleDescription(row),
    },
    {
      label: 'Ações',
      field: 'actions',
      format: row => (
        <div className='w-50 d-flex justify-content-between'>
          <Button
            color='light'
            icon='edit'
            shadow='sm'
            hoverShadow='sm'
            size='sm'
            onClick={() => handleOpenScheduleModal(row)}
          />
        </div>
      ),
      noExport: true,
    },
  ]), [handleChangeStatus, handleOpenScheduleModal]);

  return (
    <>
      <PageWrapper title={demoPages.pdvLoja.subMenu.category.text}>
        <SubHeader>
          <SubHeaderLeft>
            <Breadcrumb
              list={[
                { title: 'PDV Loja', to: '/pdvLoja' },
                { title: 'Categorias na Loja', to: 'pdvLoja/categories' },
              ]}
            />
          </SubHeaderLeft>
        </SubHeader>
        <Page container='fluid'>
          <Card>
            <CardBody className='table-responsive p-5'>
              <div className='row d-flex align-items-center mt-4 mx-1'>
                <SelectEstablishment />
                <SelectStore />
              </div>
              <TableCustom
                columns={columns}
                rows={categoriesRender}
                fileName='categorias-na-loja'
              />
            </CardBody>
          </Card>
        </Page>
      </PageWrapper>

      <ModalStoreCategorySchedule
        open={openScheduleModal}
        setIsOpen={setOpenScheduleModal}
        setIsClose={() => setOpenScheduleModal(false)}
        categorySelected={categorySelected}
      />
    </>
  );
};

export default StoreCategories;
